import { apiClient } from './client'
import type { MetaRecette } from '@/types/recipe'
import { buildStructuredSpecFromRecipeDefinitions } from '@/services/transforms/recipeDefinitionToSpec'

export type WorkflowDocumentV2 = {
  $schema: 'agentmaurice.workflow/v2'
  schema_version: 2
  workflow_id: string
  kind: 'workflow'
  title: string
  version: string
  description?: string
  forms?: AvailableRecipe['forms']
  actions: Array<Record<string, unknown>>
  [key: string]: unknown
}

export type MiniAppDocumentV2 = {
  $schema: 'agentmaurice.miniapp/v2'
  schema_version: 2
  miniapp_id: string
  kind: 'miniapp'
  title: string
  version: string
  description?: string
  ui: {
    renderer: string
    component: string
    configuration?: Record<string, unknown>
  }
  state_schema: Record<string, unknown>
  initial_state?: Record<string, unknown>
  events: Array<{
    event_id: string
    trigger: string
    workflow: {
      workflow_id?: string
      input?: Record<string, unknown>
    }
    result_to_state?: string
  }>
}

export type MiniAppProjectionV2 = {
  schema_version: 'agentmaurice.miniapp_projection/v2'
  miniapp: MiniAppDocumentV2
  status: 'active' | 'inactive'
  management_mode: 'managed' | 'unmanaged'
  owner_agent_spec_id?: string
  owner_revision: number
  content_hash: string
  resource_version: number
  created_at: string
  updated_at: string
}

type WorkflowProjectionV2 = {
  schema_version: 'agentmaurice.workflow_projection/v2'
  workflow: WorkflowDocumentV2
  status: 'active' | 'inactive'
  management_mode: 'managed' | 'unmanaged'
  owner_agent_spec_id?: string
  owner_revision: number
  content_hash: string
  resource_version: number
  created_at: string
  updated_at: string
}

export interface AvailableRecipe {
  id: string
  name: string
  version: string
  description?: string
  workflow_id?: string
  kind?: 'workflow' | 'miniapp' | string
  forms?: Array<{
    id: string
    title?: string
  }>
}

export interface RecipeExecutionResponse {
  executionId: string
  recipeId: string
  recipeName?: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  createdAt: string
  startedAt?: string
  completedAt?: string
  result?: any
  error?: string
  durationMs?: number
  usageSummary?: RecipeExecutionUsageSummary
}

export interface RecipeExecutionLogEntry {
  ts?: number
  level?: string
  component?: string
  source?: string
  message?: string
  action_id?: string
  action_type?: string
}

export interface RecipeExecutionUsageSummary {
  execution_id?: string
  workflow_id?: string
  state?: string
  duration_ms: number
  source: 'workflow_trace' | 'metering_clickhouse' | string
  complete: boolean
  actions: {
    total: number
    failed: number
    by_type?: Record<string, number>
  }
  runtime: {
    tool_calls: number
    workflow_calls: number
    code_execution_calls: number
    llm_calls: number
    total_latency_ms: number
    average_latency_ms: number
  }
  llm: {
    calls: number
    tokens_input: number
    tokens_output: number
    tokens_total: number
    estimated_cost_usd: number
    by_model?: Array<{
      model?: string
      provider?: string
      calls: number
      tokens_input: number
      tokens_output: number
      tokens_total: number
      estimated_cost_usd: number
      duration_ms: number
    }>
  }
  cost: {
    estimated_cost_usd: number
    cost_usd_micros: number
    credits_charged?: number
    monetary_cost?: {
      amount: number
      currency: string
      source?: string
    }
    monetary_cost_status: string
    source: string
    complete: boolean
  }
  action_breakdown?: Array<{
    action_id?: string
    action_type?: string
    source?: string
    name?: string
    status?: string
    error?: string
    duration_ms: number
    model?: string
    provider?: string
    tokens_input?: number
    tokens_output?: number
    tokens_total?: number
    estimated_cost_usd?: number
  }>
  metering?: {
    status: string
    events: number
    tokens_input: number
    tokens_output: number
    tokens_total: number
    cost_usd_micros: number
    latency_ms: number
  }
}

export interface RecipeExecutionStatus {
  schema_version: 'agentmaurice.workflow_execution/v2'
  execution_id: string
  workflow_id?: string
  workflow_name?: string
  state: string
  current_form_id?: string
  data?: Record<string, any>
  result?: Record<string, any>
  logs?: RecipeExecutionLogEntry[]
  trace?: Array<Record<string, unknown>>
  trace_id?: string
  duration_ms?: number
  usage_summary?: RecipeExecutionUsageSummary
  error?: string
  message?: string
}

export interface OrganizationRecipeExecutionSummary {
  execution_id: string
  workflow_id: string
  workflow_definition_id?: string
  workflow_version?: string
  kind?: 'workflow' | 'miniapp' | string
  workflow_name?: string
  state: string
  user_id?: string
  session_id?: string
  duration_ms: number
  trace_id?: string
  error_message?: string
  usage_summary?: RecipeExecutionUsageSummary
  created_at: string
  updated_at: string
}

export interface OrganizationRecipeExecutionsResponse {
  data: OrganizationRecipeExecutionSummary[]
  limit: number
  offset: number
}

function mapWorkflowProjection(projection: WorkflowProjectionV2): MetaRecette {
  const workflow = projection.workflow
  const graphDefinition = {
    ...workflow,
    id: workflow.workflow_id,
    name: workflow.title,
  }
  const metadata: Record<string, unknown> = {
    kind: workflow.kind,
    status: projection.status,
    managementMode: projection.management_mode,
    ownerAgentSpecId: projection.owner_agent_spec_id,
    ownerRevision: projection.owner_revision,
    contentHash: projection.content_hash,
    resourceVersion: projection.resource_version,
    workflow,
  }

  return {
    id: workflow.workflow_id,
    name: workflow.title,
    version: workflow.version,
    versionNumber: undefined,
    description: workflow.description,
    structured_spec: buildStructuredSpecFromRecipeDefinitions([graphDefinition]),
    metadata,
    is_active: projection.status === 'active',
    isActive: projection.status === 'active',
    createdAt: new Date(projection.created_at),
    updatedAt: new Date(projection.updated_at),
  }
}

class RecipesAPI {
  /**
   * List Workflows for an Agent
   */
  async listRecipes(
    orgId: string,
    agentId: string,
    params?: { includeInactive?: boolean },
  ): Promise<MetaRecette[]> {
    const response = await apiClient.get<{
      schema_version: 'agentmaurice.workflow_list/v2'
      items: WorkflowProjectionV2[]
    }>(
      `/v2/organizations/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/workflows`,
      {
        params: params?.includeInactive
          ? { include_inactive: true }
          : undefined,
      },
    )
    return response?.schema_version === 'agentmaurice.workflow_list/v2' && Array.isArray(response.items)
      ? response.items.map(mapWorkflowProjection)
      : []
  }

  /**
   * Get a single workflow
   */
  async getRecipe(
    orgId: string,
    agentId: string,
    workflowId: string,
  ): Promise<MetaRecette> {
    const response = await apiClient.get<WorkflowProjectionV2>(
      `/v2/organizations/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/workflows/${encodeURIComponent(workflowId)}`,
    )
    return mapWorkflowProjection(response)
  }

  async listMiniApps(
    orgId: string,
    agentId: string,
    params?: { includeInactive?: boolean },
  ): Promise<MiniAppProjectionV2[]> {
    const response = await apiClient.get<{
      schema_version: 'agentmaurice.miniapp_list/v2'
      items: MiniAppProjectionV2[]
    }>(
      `/v2/organizations/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/miniapps`,
      {
        params: params?.includeInactive
          ? { include_inactive: true }
          : undefined,
      },
    )
    return response?.schema_version === 'agentmaurice.miniapp_list/v2' && Array.isArray(response.items)
      ? response.items
      : []
  }

  async listAvailableRecipes(
    orgId: string,
    agentId: string,
    params?: { query?: string; limit?: number },
  ): Promise<AvailableRecipe[]> {
    const response = await apiClient.get<{
      schema_version: 'agentmaurice.workflow_catalog/v2'
      workflows: Array<{
        workflow_id: string
        title: string
        version: string
        description?: string
        kind: 'workflow'
        forms?: AvailableRecipe['forms']
      }>
    }>(`/v2/organizations/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/workflows/available`, {
      params,
    })

    if (response?.schema_version !== 'agentmaurice.workflow_catalog/v2') return []
    return Array.isArray(response.workflows)
      ? response.workflows.map((workflow) => ({
          id: workflow.workflow_id,
          workflow_id: workflow.workflow_id,
          name: workflow.title,
          version: workflow.version,
          description: workflow.description,
          kind: workflow.kind,
          forms: workflow.forms,
        }))
      : []
  }

  async getScopedRecipeExecutionStatus(
    orgId: string,
    agentId: string,
    executionId: string,
    params?: {
      include_logs?: boolean
      include_trace?: boolean
    },
  ): Promise<RecipeExecutionStatus> {
    return apiClient.get(
      `/v2/organizations/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/workflow-executions/${encodeURIComponent(executionId)}`,
      {
        params,
      },
    )
  }


  async listOrganizationRecipeExecutions(
    orgId: string,
    agentId: string,
    params?: {
      limit?: number
      offset?: number
      workflow_id?: string
      user_id?: string
      session_id?: string
      state?: string
      has_trace_id?: boolean
      has_error?: boolean
      created_after?: string
      created_before?: string
    },
  ): Promise<OrganizationRecipeExecutionsResponse> {
    const response = await apiClient.get<{
      schema_version: 'agentmaurice.workflow_execution_list/v2'
      items: OrganizationRecipeExecutionSummary[]
      limit: number
      offset: number
    }>(`/v2/organizations/${encodeURIComponent(orgId)}/agents/${encodeURIComponent(agentId)}/workflow-executions`, {
      params,
    })
    return {
      data: Array.isArray(response?.items) ? response.items : [],
      limit: response?.limit ?? params?.limit ?? 20,
      offset: response?.offset ?? params?.offset ?? 0,
    }
  }
}

export const recipesAPI = new RecipesAPI()
